// Onboarding / Register — the adopt-a-Mao flow. Account-required (no guests).
//
// Step order (logged out):  1) Kingdom → 2) Name + coat → 3) Email + password
//                           → 4) Role. (Logged in via Google: the email step is
// skipped.) All roles are free common-worker trades — they create the account
// and start playing. (CR-7: paid head-start roles retired; USD buys coin packs
// in the Shop instead.)
//
// This modal IS the registration wall: it shows on the PLAY view whenever the
// player can't play yet (not logged in, or logged in without a cat) and hides
// once they have an account + a cat. A × always escapes back to Home.

import { CatColors, KingdomConfig, KingdomOrder, Roles, RoleOrder } from '../config/balance.js'

// CR-7: USD only buys coin packs now — paid head-start roles are retired, so
// onboarding offers the free common-worker roles only.
const FREE_ROLES = RoleOrder.filter((id) => Roles[id].priceUsd === 0)
import { completeOnboarding, isOnboarded } from '../game/profileManager.js'
import { Events, emit, on } from './eventBus.js'
import { getAccountInfo, signup, manualPush } from '../game/accountManager.js'
import { applyStartingPackage } from '../game/startingPackage.js'
import { getActivePage, setActivePage } from './siteRouter.js'

// The email/password step is dropped once the player already has a session
// (e.g. they signed in with Google first, then pick their kingdom + cat).
const stepOrder = () =>
  getAccountInfo().loggedIn ? ['kingdom', 'cat', 'role'] : ['kingdom', 'cat', 'account', 'role']

// Short labels under the progress circles at the top of the card.
const STEP_LABELS = { kingdom: 'Kingdom', cat: 'Your Mao', account: 'Account', role: 'Role' }

export function mountOnboardingPanel({ root }) {
  const state = {
    stepIdx: 0,
    homeKingdom: null,
    catName: '',
    catColorId: CatColors[0].id,
    gender: null,
    email: '',
    password: '',
    role: null,
    attempted: false, // true after a failed final submit → incomplete steps get flagged
  }

  root.innerHTML = template()

  const els = {
    title: root.querySelector('[data-onboard-step-title]'),
    body: root.querySelector('[data-onboard-step-body]'),
    back: root.querySelector('[data-onboard-back]'),
    next: root.querySelector('[data-onboard-next]'),
    progress: root.querySelector('[data-onboard-progress]'),
    stepper: root.querySelector('[data-onboard-stepper]'),
    toast: root.querySelector('[data-onboard-toast]'),
    progressFill: root.querySelector('[data-onboard-progress-fill]'),
    error: root.querySelector('[data-onboard-error]'),
  }

  const currentStep = () => stepOrder()[state.stepIdx]
  const isLastStep = () => state.stepIdx === stepOrder().length - 1
  const setError = (t) => { els.error.textContent = t || '' }

  const reduceMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  // 1-2-3-4 progress graphic. Every circle is a button — jump to any step to edit
  // or change an answer. ✓ = complete, ! = incomplete (flagged after a failed submit).
  const renderStepper = () => {
    const order = stepOrder()
    els.stepper.innerHTML = order.map((id, i) => {
      const valid = STEPS[id].canAdvance(state)
      const current = i === state.stepIdx
      const attention = state.attempted && !valid && !current
      const status = current ? 'current' : valid ? 'done' : attention ? 'attention' : 'todo'
      const mark = status === 'done' ? '✓' : status === 'attention' ? '!' : i + 1
      const note = status === 'done' ? ' (complete)' : status === 'attention' ? ' (needs attention)' : ''
      return `<li class="hud__step ${status}" data-step-item="${i}">
        <button type="button" class="hud__step-btn" data-step-go="${i}"${current ? ' aria-current="step"' : ''}
                aria-label="Step ${i + 1}: ${STEP_LABELS[id] ?? id}${note}">
          <span class="hud__step-dot">${mark}</span>
          <span class="hud__step-label">${STEP_LABELS[id] ?? id}</span>
        </button>
      </li>`
    }).join('')
  }

  // Slide from the current step to `idx`. Forward slides left, backward slides right.
  const goTo = (idx) => {
    const last = stepOrder().length - 1
    const target = Math.max(0, Math.min(last, idx))
    if (target === state.stepIdx) return
    const dir = target > state.stepIdx ? 1 : -1
    state.stepIdx = target
    render({ dir })
  }

  const render = ({ dir = 0 } = {}) => {
    const order = stepOrder()
    const stepId = order[state.stepIdx]
    els.progress.textContent = `Step ${state.stepIdx + 1} of ${order.length}`
    els.progressFill.style.width = `${Math.round((state.stepIdx / (order.length - 1)) * 100)}%`
    renderStepper()

    els.next.style.display = '' // restore (the verify-email screen hides it)
    const renderer = STEPS[stepId]
    els.title.textContent = renderer.title
    // Re-trigger the title's small slide/fade.
    els.title.classList.remove('swap'); void els.title.offsetWidth; els.title.classList.add('swap')

    // Old content slides out as a "ghost" while the new content slides in.
    const animate = dir !== 0 && els.body.childNodes.length > 0 && !reduceMotion()
    let ghost = null
    if (animate) {
      ghost = document.createElement('div')
      ghost.className = `hud__slide hud__slide--ghost ${dir > 0 ? 'to-left' : 'to-right'}`
      ;[...els.body.childNodes].forEach((n) => ghost.appendChild(n))
    }
    els.body.innerHTML =
      `<div class="hud__slide hud__slide--in${animate ? (dir > 0 ? ' from-right' : ' from-left') : ''}">${renderer.body(state)}</div>`
    renderer.bind(state, els, advance) // bind BEFORE adding the ghost so selectors only see live content
    if (ghost) {
      els.body.appendChild(ghost)
      ghost.addEventListener('animationend', () => ghost.remove(), { once: true })
    }
    setError('')

    // Back is hidden (not just disabled) on the first step so it never looks
    // broken; visibility keeps the footer layout so Next stays right-aligned.
    els.back.style.visibility = state.stepIdx === 0 ? 'hidden' : 'visible'
    // The final button is always live: pressing it with something missing explains
    // what and slides you back to it (see finishAdoption).
    els.next.disabled = isLastStep() ? false : !renderer.canAdvance(state)
    els.next.textContent = isLastStep() ? finalCtaText(state) : 'Next →'
  }

  // Friendly pop-up pill that floats over the card for a few seconds.
  let toastTimer = null
  const showToast = (message) => {
    els.toast.textContent = message
    els.toast.classList.remove('show'); void els.toast.offsetWidth; els.toast.classList.add('show')
    clearTimeout(toastTimer)
    toastTimer = setTimeout(() => els.toast.classList.remove('show'), 5200)
  }

  // Click a circle → slide there. Typing/choosing refreshes ✓/! live.
  els.stepper.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-step-go]')
    if (btn) goTo(Number(btn.dataset.stepGo))
  })
  els.body.addEventListener('input', () => renderStepper())

  function advance() { render() }

  function resetFlow() {
    Object.assign(state, {
      stepIdx: 0, homeKingdom: null, catName: '', catColorId: CatColors[0].id, gender: null,
      email: '', password: '', role: null, attempted: false,
    })
  }

  // Gating wall — shows on PLAY when the player can't yet play.
  const shouldShow = () => getActivePage() === 'play' && (!getAccountInfo().loggedIn || !isOnboarded())
  const updateVisibility = () => {
    if (shouldShow()) {
      if (!root.classList.contains('active')) { resetFlow(); root.classList.add('active'); render() }
    } else {
      root.classList.remove('active')
    }
  }

  els.back.addEventListener('click', () => goTo(state.stepIdx - 1))

  // Always-available escape → back to Home (you still need an account to PLAY).
  root.querySelector('[data-onboard-close]').addEventListener('click', () => {
    root.classList.remove('active')
    setActivePage('home')
  })

  els.next.addEventListener('click', async () => {
    if (isLastStep()) { await finishAdoption(); return }
    if (!STEPS[currentStep()].canAdvance(state)) return
    goTo(state.stepIdx + 1)
  })

  async function finishAdoption() {
    // Check every step, in order. The first incomplete one gets a pop-up message and
    // we slide back to it (its circle turns red with a "!").
    const order = stepOrder()
    const badIdx = order.findIndex((id) => !STEPS[id].canAdvance(state))
    if (badIdx !== -1) {
      state.attempted = true
      const message = STEPS[order[badIdx]].missing(state)
      if (badIdx === state.stepIdx) render()
      else goTo(badIdx)
      showToast(message)
      const item = els.stepper.querySelector(`[data-step-item="${badIdx}"]`)
      item?.classList.add('shake')
      return
    }
    const role = Roles[state.role]
    if (!role) return

    // Email path (not yet logged in) → create the account first.
    if (!getAccountInfo().loggedIn) {
      const email = state.email.trim()
      if (!email || state.password.length < 6) { setError('Enter an email and a password (6+ characters).'); return }
      els.next.disabled = true
      setError('Creating your account…')
      const res = await signup(email, state.password)
      els.next.disabled = false
      if (!res.ok) { setError(res.reason || 'Could not create account.'); return }
      // Email confirmation is ON → no session yet. Show a verify screen instead
      // of dropping into the game; they finish setup after verifying + logging in.
      if (res.needsConfirmation) { showVerifyEmail(email); return }
    }

    completeOnboarding({
      catName: state.catName,
      catColorId: state.catColorId,
      gender: state.gender,
      homeKingdom: state.homeKingdom,
      title: role.title,
      classId: role.classId,
    })
    applyStartingPackage(state.role) // no-op for free roles
    try { await manualPush() } catch { /* sync ticker retries */ }
    root.classList.remove('active')
  }

  // Email-confirmation path: account created but not yet verified. Tell them to
  // check their inbox + log in. After verifying they return logged-in and the
  // gate re-shows onboarding (now the short logged-in path) to finish setup.
  function showVerifyEmail(emailAddr) {
    els.stepper.innerHTML = ''
    els.title.textContent = 'Check Your Email ✉️'
    els.progress.textContent = ''
    els.progressFill.style.width = '100%'
    els.next.style.display = 'none'
    els.back.style.visibility = 'hidden'
    setError('')
    els.body.innerHTML = `
      <div class="hud__onboard-note" style="line-height:1.6">
        We sent a verification link to <strong>${escapeHtml(emailAddr)}</strong>.
        Open it to confirm your account, then come back and <strong>log in</strong>
        to enter your kingdom.
      </div>
      <div class="hud__onboard-login-row" style="margin-top:18px">
        <button type="button" class="hud__onboard-btn" data-verify-login>Log in</button>
      </div>`
    els.body.querySelector('[data-verify-login]')?.addEventListener('click', () => {
      root.classList.remove('active')
      emit(Events.OpenLogin)
    })
  }

  // Header "Register", home CTA, and the login modal's "Register" link all open
  // a fresh flow. Auth/page changes re-evaluate the gate.
  on(Events.StartAdoption, () => { resetFlow(); root.classList.add('active'); render() })
  on(Events.AccountReady, updateVisibility)
  on(Events.PageChange, updateVisibility)
  updateVisibility()
}

function finalCtaText() {
  return getAccountInfo().loggedIn ? 'Adopt & Play' : 'Create Account & Adopt'
}

// ── Step renderers ─────────────────────────────────────────────────────────
const STEPS = {
  kingdom: {
    title: 'Choose Your Kingdom 王国',
    body: (state) => `
      <div class="hud__onboard-cards">
        ${KingdomOrder.map((id) => {
          const k = KingdomConfig[id]
          const sel = state.homeKingdom === id ? 'selected' : ''
          return `
            <button class="hud__kingdom-card ${sel}" data-kingdom-pick="${id}" style="--accent:${k.accent}">
              <div class="name">${k.name}</div>
              <div class="sub">${k.subtitle}</div>
            </button>
          `
        }).join('')}
      </div>
      <div class="hud__onboard-note">Choice is sticky. Defection later costs 50,000m.</div>
    `,
    bind: (state, _els, advance) => {
      document.querySelectorAll('[data-kingdom-pick]').forEach((b) => {
        b.addEventListener('click', () => { state.homeKingdom = b.dataset.kingdomPick; advance() })
      })
    },
    missing: (state) => (state.homeKingdom ? '' : 'Choose your kingdom — Wei, Han, or Wu.'),
  },

  cat: {
    title: 'Name Your Mao 收养',
    body: (state) => {
      const swatches = CatColors.map((c) => {
        const sel = c.id === state.catColorId ? 'selected' : ''
        return `<button class="hud__cat-swatch ${sel}" data-cat-color="${c.id}" title="${c.label}" style="background:#${c.body.toString(16).padStart(6, '0')}"></button>`
      }).join('')
      return `
        <label class="hud__onboard-label">Name your cat</label>
        <input type="text" name="cat-name" maxlength="20" placeholder="Whiskers..." value="${escapeHtml(state.catName)}" />
        <label class="hud__onboard-label" style="margin-top:18px">Pick a coat</label>
        <div class="hud__cat-swatches">${swatches}</div>
        <label class="hud__onboard-label" style="margin-top:18px">Male or female cat? <span style="opacity:.7;text-transform:none;letter-spacing:0">(permanent — it decides who your Mao may marry)</span></label>
        <div class="hud__cat-swatches" data-gender-pick style="gap:10px">
          ${['MALE', 'FEMALE'].map((g) => `<button type="button" class="hud__cat-swatch ${state.gender === g ? 'selected' : ''}" data-gender="${g}" style="width:auto;min-width:110px;padding:8px 14px;border-radius:6px;color:#fff;background:rgba(255,255,255,.08);font-family:inherit;font-weight:600;font-size:15px">${g === 'MALE' ? '♂ Male' : '♀ Female'}</button>`).join('')}
        </div>
      `
    },
    bind: (state, els) => {
      const input = els.body.querySelector('input[name="cat-name"]')
      input.addEventListener('input', (e) => {
        state.catName = e.target.value.trim()
        els.next.disabled = !STEPS.cat.canAdvance(state)
      })
      els.body.querySelectorAll('[data-gender]').forEach((b) => {
        b.addEventListener('click', () => {
          state.gender = b.dataset.gender
          els.body.querySelectorAll('[data-gender]').forEach((x) => x.classList.toggle('selected', x.dataset.gender === state.gender))
          els.next.disabled = !STEPS.cat.canAdvance(state)
        })
      })
      els.body.querySelectorAll('[data-cat-color]').forEach((b) => {
        b.addEventListener('click', () => {
          state.catColorId = b.dataset.catColor
          els.body.querySelectorAll('[data-cat-color]').forEach((x) =>
            x.classList.toggle('selected', x.dataset.catColor === state.catColorId),
          )
        })
      })
    },
    missing: (state) => (state.catName.trim().length === 0 ? 'Give your Mao a name.' : state.gender ? '' : 'Choose whether your Mao is a male or a female cat.'),
  },

  account: {
    title: 'Create Your Account 注册',
    body: (state) => `
      <div class="hud__onboard-note" style="margin-bottom:14px">You'll use these to log in and keep your kingdom on any device.</div>
      <label class="hud__onboard-label">Email</label>
      <input type="email" name="ob-email" placeholder="you@example.com" autocomplete="email" value="${escapeHtml(state.email)}">
      <label class="hud__onboard-label" style="margin-top:14px">Password</label>
      <input type="password" name="ob-password" placeholder="6+ characters" autocomplete="new-password" value="${escapeHtml(state.password)}">
      <div class="hud__onboard-login-row" style="margin-top:16px">
        Already have an account?
        <button type="button" class="hud__onboard-login-link" data-onboard-login>Log in</button>
      </div>
    `,
    bind: (state, els) => {
      const e = els.body.querySelector('input[name="ob-email"]')
      const p = els.body.querySelector('input[name="ob-password"]')
      const sync = () => { els.next.disabled = !STEPS.account.canAdvance(state) }
      e.addEventListener('input', (ev) => { state.email = ev.target.value; sync() })
      p.addEventListener('input', (ev) => { state.password = ev.target.value; sync() })
      els.body.querySelector('[data-onboard-login]')?.addEventListener('click', () => emit(Events.OpenLogin))
    },
    missing: (state) => {
      const email = state.email.trim()
      if (!email) return 'Enter your email address.'
      if (!/^\S+@\S+\.\S+$/.test(email)) return 'That email address doesn’t look right.'
      if (state.password.length < 6) return 'Choose a password with at least 6 characters.'
      return ''
    },
  },

  role: {
    title: 'Choose Your Role 身份',
    body: (state) => `
      <div class="hud__onboard-note" style="margin-bottom:12px">
        Every Mao starts as a common worker — pick the trade that suits you. You'll
        rise through study, honor, and coin once you're in the game.
      </div>
      <div class="hud__role-grid">
        ${FREE_ROLES.map((id) => {
          const r = Roles[id]
          const sel = state.role === id ? 'selected' : ''
          return `
            <button class="hud__role-card ${sel}" data-role-pick="${id}">
              <div class="hud__role-head">
                <span class="name">${r.label}</span>
                <span class="price">Free</span>
              </div>
              <div class="desc">${r.blurb}</div>
            </button>
          `
        }).join('')}
      </div>
    `,
    bind: (state, _els, advance) => {
      document.querySelectorAll('[data-role-pick]').forEach((b) => {
        b.addEventListener('click', () => {
          state.role = b.dataset.rolePick
          advance()
        })
      })
    },
    missing: (state) => (Roles[state.role] ? '' : 'Choose a role to begin your journey.'),
  },
}

// canAdvance is simply "nothing missing" — one source of truth per step.
for (const step of Object.values(STEPS)) step.canAdvance = (state) => !step.missing(state)

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c])
}

function template() {
  return `
    <div class="hud__onboard-card">
      <button class="hud__onboard-close" data-onboard-close aria-label="Close — back to home">×</button>
      <ol class="hud__stepper" data-onboard-stepper aria-label="Adoption progress"></ol>
      <div class="hud__onboard-header">
        <h2 data-onboard-step-title>—</h2>
        <span data-onboard-progress>Adopt a Mao</span>
      </div>
      <div class="hud__onboard-progress-bar"><div class="hud__onboard-progress-fill" data-onboard-progress-fill></div></div>
      <div class="hud__onboard-body" data-onboard-step-body></div>
      <div class="hud__onboard-toast" role="alert" aria-live="assertive" data-onboard-toast></div>
      <div class="hud__onboard-error" data-onboard-error></div>
      <div class="hud__onboard-footer">
        <button class="hud__onboard-btn ghost" data-onboard-back disabled>← Back</button>
        <button class="hud__onboard-btn" data-onboard-next disabled>Next →</button>
      </div>
    </div>
  `
}
